import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Newspaper, Music4, Youtube, Star, Users, Home,
  ShoppingBag, Inbox, Mail, Settings, LogOut, ExternalLink, Loader2,
} from "lucide-react";
import { adminApi, setToken, getToken, formatApiError } from "./api";
import { LogoMark } from "../components/cobain/Nav";
import Dashboard from "./pages/Dashboard";
import Articles from "./pages/Articles";
import Music from "./pages/Music";
import Latest from "./pages/Latest";
import Featured from "./pages/Featured";
import TeamAdmin from "./pages/Team";
import Homepage from "./pages/Homepage";
import ShopAdmin from "./pages/Shop";
import Inquiries from "./pages/Inquiries";
import Newsletter from "./pages/Newsletter";
import SettingsPage from "./pages/Settings";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/editorial", label: "Editorial", icon: Newspaper },
  { to: "/admin/music", label: "Music", icon: Music4 },
  { to: "/admin/latest", label: "Latest / YouTube", icon: Youtube },
  { to: "/admin/featured", label: "Featured Media", icon: Star },
  { to: "/admin/team", label: "Team", icon: Users },
  { to: "/admin/homepage", label: "Homepage", icon: Home },
  { to: "/admin/shop", label: "Cobain Culture", icon: ShoppingBag },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/auth/login", { email, password });
      setToken(data.token);
      onLogin(data.user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-5" data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <LogoMark size={52} />
          <h1 className="mt-5 font-display text-3xl text-cream tracking-wide">THE COBAIN REPORT</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-tan">OPERATOR CMS</p>
        </div>
        <form onSubmit={submit} className="border border-woodborder bg-card/50 p-7 space-y-5" data-testid="admin-login-form" noValidate>
          <div>
            <label className="block font-mono text-[10px] tracking-[0.22em] text-tan mb-1.5">EMAIL</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full bg-obsidian/70 border border-woodborder focus:border-burnt text-cream text-sm px-4 py-3 outline-none rounded-sm"
              placeholder="admin@thecobainreport.com" data-testid="admin-email-input"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] tracking-[0.22em] text-tan mb-1.5">PASSWORD</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
              className="w-full bg-obsidian/70 border border-woodborder focus:border-burnt text-cream text-sm px-4 py-3 outline-none rounded-sm"
              placeholder="••••••••••" data-testid="admin-password-input"
            />
          </div>
          {error && <p className="font-mono text-[11px] text-burnt-hover" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit" disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-burnt hover:bg-burnt-hover text-cream font-mono text-xs tracking-[0.25em] px-6 py-4 rounded-full transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "AUTHENTICATING..." : "ENTER THE CMS"}
          </button>
        </form>
        <p className="mt-6 text-center font-mono text-[9px] tracking-[0.25em] text-clay">AUTHORIZED OPERATORS ONLY</p>
      </div>
    </div>
  );
}

export default function AdminApp() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Cobain CMS — Operator";
    const robots = document.createElement("meta");
    robots.name = "robots";
    robots.content = "noindex, nofollow";
    document.head.appendChild(robots);
    return () => {
      document.title = "The Cobain Report — Detroit Culture Media";
      document.head.removeChild(robots);
    };
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setUser(false);
      return;
    }
    adminApi.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      setToken(null);
      setUser(false);
    });
  }, []);

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
    navigate("/admin");
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center" data-testid="admin-loading">
        <Loader2 size={28} className="animate-spin text-burnt" />
      </div>
    );
  }
  if (user === false) return <Login onLogin={setUser} />;

  return (
    <div className="min-h-screen bg-obsidian text-cream font-body" data-testid="admin-shell">
      <div className="lg:flex">
        <aside className="hidden lg:flex flex-col w-60 shrink-0 min-h-screen border-r border-woodborder bg-wood/40 sticky top-0 h-screen">
          <div className="flex items-center gap-3 px-5 h-[72px] border-b border-woodborder">
            <LogoMark size={30} />
            <div>
              <p className="font-display text-base text-cream leading-none tracking-wide">COBAIN CMS</p>
              <p className="font-mono text-[8px] tracking-[0.3em] text-tan mt-1">OPERATOR</p>
            </div>
          </div>
          <nav className="flex-1 py-4 overflow-y-auto" data-testid="admin-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-3 font-mono text-[11px] tracking-[0.15em] transition-colors border-l-2 ${
                    isActive ? "text-cream bg-burnt/10 border-burnt" : "text-clay hover:text-sand border-transparent"
                  }`
                }
              >
                <n.icon size={15} />
                {n.label.toUpperCase()}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-woodborder p-4 space-y-2">
            <Link to="/" className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-clay hover:text-tan transition-colors" data-testid="admin-view-site">
              <ExternalLink size={13} /> VIEW PUBLIC SITE
            </Link>
            <button onClick={logout} className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-clay hover:text-burnt-hover transition-colors" data-testid="admin-logout">
              <LogOut size={13} /> LOG OUT
            </button>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <header className="lg:hidden sticky top-0 z-40 bg-obsidian/95 backdrop-blur border-b border-woodborder">
            <div className="flex items-center justify-between px-4 h-14">
              <div className="flex items-center gap-2">
                <LogoMark size={26} />
                <span className="font-display text-base text-cream tracking-wide">COBAIN CMS</span>
              </div>
              <div className="flex items-center gap-4">
                <Link to="/" className="text-clay hover:text-tan" data-testid="admin-view-site-mobile" aria-label="View site"><ExternalLink size={17} /></Link>
                <button onClick={logout} className="text-clay hover:text-burnt-hover" data-testid="admin-logout-mobile" aria-label="Log out"><LogOut size={17} /></button>
              </div>
            </div>
            <nav className="flex gap-1 overflow-x-auto no-scrollbar px-3 pb-2" data-testid="admin-nav-mobile">
              {NAV.map((n) => (
                <NavLink
                  key={n.to} to={n.to} end={n.end}
                  className={({ isActive }) =>
                    `shrink-0 font-mono text-[10px] tracking-[0.12em] px-3 py-2 rounded-full ${isActive ? "bg-burnt text-cream" : "text-clay"}`
                  }
                >
                  {n.label.toUpperCase()}
                </NavLink>
              ))}
            </nav>
          </header>
          <main className="p-5 md:p-10 max-w-[1200px]" data-testid="admin-main">
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="editorial" element={<Articles />} />
              <Route path="music" element={<Music />} />
              <Route path="latest" element={<Latest />} />
              <Route path="featured" element={<Featured />} />
              <Route path="team" element={<TeamAdmin />} />
              <Route path="homepage" element={<Homepage />} />
              <Route path="shop" element={<ShopAdmin />} />
              <Route path="inquiries" element={<Inquiries />} />
              <Route path="newsletter" element={<Newsletter />} />
              <Route path="settings" element={<SettingsPage user={user} onLogout={logout} />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
}
